import React, { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import { 
  CommandDialog, 
  CommandInput, 
  CommandList, 
  CommandEmpty, 
  CommandGroup, 
  CommandItem 
} from '../ui/command';
import { searchAPI } from '../../lib/api';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../ui/tooltip';

const GlobalSearch = ({ collapsed }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const down = (e) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        console.log('Fetching search results for:', query);
        const response = await searchAPI.global(query);
        console.log('Search results received:', response.data);
        setResults(response.data);
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const handleSelect = (href) => {
    setOpen(false);
    setQuery('');
    navigate(href);
  };

  const SearchButton = (
    <button
      onClick={() => setOpen(true)}
      className={cn(
        "flex items-center gap-3 px-3 py-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-all duration-200 mb-2 group border border-white/5",
        collapsed ? "h-10 w-10 justify-center mx-auto" : "w-[calc(100%-16px)] mx-2"
      )}
    >
      <Search className="h-5 w-5 shrink-0 transition-colors group-hover:text-amber-400" />
      {!collapsed && (
        <div className="flex items-center justify-between flex-1">
          <span className="text-sm truncate">Search...</span>
          <kbd className="pointer-events-none select-none flex h-5 items-center gap-1 rounded border border-white/10 bg-white/5 px-1.5 font-mono text-[10px] font-medium text-slate-500 opacity-100">
            <span className="text-xs">⌘</span>K
          </kbd>
        </div>
      )}
    </button>
  );

  return (
    <>
      {collapsed ? (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            {SearchButton}
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={10} className="font-semibold bg-slate-900 border-white/10 text-white">
            Search <span className="ml-2 text-xs opacity-50">⌘K</span>
          </TooltipContent>
        </Tooltip>
      ) : (
        SearchButton
      )}

      <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
        <CommandInput 
          placeholder="Search employees, assets, and pages..." 
          value={query}
          onValueChange={setQuery}
        />
        <CommandList className="max-h-[450px] pb-3">
          <CommandEmpty className="py-12 text-center text-slate-500">
            <div className="flex flex-col items-center gap-2">
              <Search className="h-8 w-8 opacity-20" />
              <span>No results found for "{query}"</span>
            </div>
          </CommandEmpty>
          
          {loading && (
            <div className="p-8 text-center text-sm text-slate-500">
              <div className="flex items-center justify-center gap-2">
                <div className="h-4 w-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                <span>Searching...</span>
              </div>
            </div>
          )}
          
          {results.length > 0 && (
            <CommandGroup heading="Results">
              {results.map((result) => (
                <CommandItem
                  key={result.id}
                  onSelect={() => handleSelect(result.href)}
                  className="flex flex-col items-start gap-1 py-3 px-4 cursor-pointer data-[selected=true]:bg-amber-50 rounded-md mx-1"
                >
                  <div className="flex items-center gap-2 w-full">
                    <span className="font-semibold text-slate-900 group-data-[selected=true]:text-amber-600">
                      {result.title}
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-widest bg-slate-100 px-2 py-0.5 rounded-full text-slate-500 ml-auto border border-slate-200/50">
                      {result.type}
                    </span>
                  </div>
                  <span className="text-xs text-slate-500 font-medium">{result.subtitle}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
};

export default GlobalSearch;
